import type Talk from 'talkjs';
import type { ChatPhotoSaveDestination } from '../../types/chats';

export type { ChatPhotoSaveDestination };

const NON_IMAGE_EXTENSION =
  /\.(pdf|doc|docx|xls|xlsx|zip|mp4|mov|webm|avi|mp3|wav|m4a|aac)(\?|$)/i;
const IMAGE_EXTENSION = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)(\?|$)/i;

type ChatMediaMessage = Pick<Talk.Message, 'attachment' | 'type'>;

function decodeUriComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function getChatAttachmentUrl(message: ChatMediaMessage): string | null {
  if (message.type && message.type !== 'media') return null;
  return message.attachment?.url ?? null;
}

export function isChatImageMessage(message: ChatMediaMessage): boolean {
  const url = getChatAttachmentUrl(message);
  if (!url) return false;
  if (NON_IMAGE_EXTENSION.test(url)) return false;
  if (IMAGE_EXTENSION.test(url)) return true;
  return !!message.attachment?.dimensions;
}

export function getChatPhotoFileName(url: string): string {
  const fallback = () => `chat_photo_${Date.now()}.jpg`;

  try {
    const { pathname } = new URL(url);
    const lastSegment = pathname.split('/').pop();
    if (!lastSegment) return fallback();

    const decoded = decodeUriComponentSafe(lastSegment);
    const baseName = decoded.split('/').pop() || decoded;
    if (/\.[a-z0-9]+$/i.test(baseName)) return baseName;
  } catch {
    return fallback();
  }

  return fallback();
}

const EXT_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

export function getChatPhotoMimeType(
  fileName: string,
  contentType?: string | null,
): string {
  const normalized = contentType?.split(';')[0]?.trim().toLowerCase();
  if (normalized?.startsWith('image/')) return normalized;

  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext && EXT_TO_MIME[ext]) return EXT_TO_MIME[ext];

  return 'image/jpeg';
}
